import React, { useRef } from 'react';
import { useIntl } from 'react-intl';
import type { MessageDescriptor } from 'react-intl';
import { PiX } from 'react-icons/pi';
import { TOOLTIP_PLACEMENT_RIGHT, Tooltip } from '@aibuild/ui';
import { usePortalContainer } from '../environment';
import ChromeButton from './ChromeButton';
import { useChromeLayout } from './ChromeContext';
import { MouseIcon } from './icons';
import type { MousePart } from './icons';
import { messages } from './messages';
import { SHORTCUTS } from './shortcuts';
import { usePopover } from './usePopover';
import { Card, CardHeader, Heading, Hint, Key, KeyText, NavSlot, Row, Toggle } from './NavigationHelp.styled';

interface Gesture {
  name: MessageDescriptor;
  hint: MessageDescriptor;
  part?: MousePart;
}

const MOUSE: readonly Gesture[] = [
  { name: messages.navSelect, hint: messages.navSelectHint, part: 'left' },
  { name: messages.navOrbit, hint: messages.navOrbitHint, part: 'left' },
  { name: messages.navZoom, hint: messages.navZoomHint, part: 'wheel' },
  { name: messages.navPan, hint: messages.navPanHint, part: 'right' },
];

const TOUCH: readonly Gesture[] = [
  { name: messages.navSelect, hint: messages.navSelectTouchHint },
  { name: messages.navOrbit, hint: messages.navOrbitTouchHint },
  { name: messages.navZoom, hint: messages.navZoomTouchHint },
  { name: messages.navPan, hint: messages.navPanTouchHint },
];

const NavigationHelp: React.FC = () => {
  const intl = useIntl();
  const portal = usePortalContainer();
  const { narrow, coarse } = useChromeLayout();
  const cardRef = useRef<HTMLDivElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const help = usePopover(toggleRef, cardRef);
  const label = intl.formatMessage(messages.navigation);
  return (
    <NavSlot>
      <Tooltip
        content={label}
        placement={TOOLTIP_PLACEMENT_RIGHT}
        open={help.tooltipOpen}
        portal
        portalContainer={portal}
      >
        <Toggle
          ref={toggleRef}
          type="button"
          aria-label={label}
          aria-expanded={help.open}
          $open={help.open}
          $narrow={narrow}
          onClick={help.toggle}
          data-testid="model-viewer__navigation"
        >
          <MouseIcon />
        </Toggle>
      </Tooltip>
      {help.open && (
        <Card ref={cardRef} role="dialog" aria-label={label} data-testid="model-viewer__navigation-card">
          <CardHeader>
            <Heading>{intl.formatMessage(messages.navigationMouse)}</Heading>
            <ChromeButton
              label={intl.formatMessage(messages.navigationClose)}
              icon={<PiX />}
              onClick={help.close}
              testId="model-viewer__navigation-close"
            />
          </CardHeader>
          {(coarse ? TOUCH : MOUSE).map(({ name, hint, part }) => (
            <Row key={name.id}>
              <MouseIcon part={part} />
              <span>
                {intl.formatMessage(name)}
                <Hint>{intl.formatMessage(hint)}</Hint>
              </span>
            </Row>
          ))}
          {!coarse && (
            <>
              <Heading>{intl.formatMessage(messages.navigationKeyboard)}</Heading>
              {SHORTCUTS.map(({ keys, description }) => (
                <Row key={keys}>
                  <Key>{keys}</Key>
                  <KeyText>{intl.formatMessage(description)}</KeyText>
                </Row>
              ))}
            </>
          )}
        </Card>
      )}
    </NavSlot>
  );
};

export default NavigationHelp;
